import * as React from "react";

import {
  Box,
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  UseDisclosureReturn,
  VStack,
} from "@chakra-ui/react";
import {
  ChiefComplaintInput,
  ClinicalSupportInput,
  HUCInput,
  NurseInput,
  RoomInput,
  ScribeInput,
  SupervisorInput,
  TreatingProviderInput,
} from "./patient-details-inputs";
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form";
import { EncounterAvatar, Loading } from "shared";
import { RoomBedDto, usePatientDetails } from "modules/in-process/api";

import { MedicalStaff } from "modules/in-process/types";
import { Tabs } from "shared/tabs/Tabs";
import { extractApiErrorMessage } from "utils";
import { genericErrors } from "messages";
import { usePatchEncounterField } from "modules/reception/api";
import { usePermissions } from "contexts";
import { useToast } from "hooks";

type PatientDetailsModalProps = Pick<
  UseDisclosureReturn,
  "isOpen" | "onClose"
> & { encounterId: string };
type PatientDetailsModalContentProps = Pick<
  PatientDetailsModalProps,
  "onClose" | "encounterId"
>;

type PatientDetailsForm = {
  roomBed: RoomBedDto | null;
  nurse: MedicalStaff | null;
  provider: MedicalStaff | null;
  supervisor: MedicalStaff | null;
  observationSupervisor: MedicalStaff | null;
  chargeNurse: MedicalStaff | null;
  admittingProvider: MedicalStaff | null;
  observationProvider: MedicalStaff | null;
  observationNurse: MedicalStaff | null;
  chiefComplaint: string | undefined;
  huc: MedicalStaff | null;
  scribe: MedicalStaff | null;
  observationScribe: MedicalStaff | null;
  clinicalSupport: MedicalStaff | null;
  observationClinicalSupport: MedicalStaff | null;
};

type ProvidersFormProps = {
  type: "ER" | "Observation";
  encounterId: string;
  onClose: () => void;
};

const boxInputStyles = {
  borderRadius: "10px",
  bg: "white",
  mb: 4,
};

function ProvidersForm({ type, encounterId, onClose }: ProvidersFormProps) {
  const { scope } = usePermissions();
  const isReadOnly = !scope("inprocess").isEditable;

  const { control } = useFormContext<PatientDetailsForm>();

  if (type === "ER") {
    return (
      <Box>
        <Box>ER Nurse Chart</Box>
        <Box {...boxInputStyles} mb={0}>
          <Controller
            render={({ field: { value, onChange, ...rest } }) => (
              <NurseInput
                {...rest}
                value={value}
                onChange={onChange}
                encounterId={encounterId}
                encounterUserType="Nurse"
                userType="Nurse"
                onCloseDetailsModal={onClose}
                disabled={isReadOnly}
              />
            )}
            name="nurse"
            control={control}
          />
        </Box>
        <Box {...boxInputStyles}>
          <Controller
            render={({ field: { value, onChange, ...rest } }) => (
              <ClinicalSupportInput
                {...rest}
                value={value}
                onChange={onChange}
                encounterId={encounterId}
                encounterUserType="ClinicalSupport"
                userType="ClinicalSupport"
                onCloseDetailsModal={onClose}
                disabled={isReadOnly}
              />
            )}
            name="clinicalSupport"
            control={control}
          />
        </Box>

        <Box>ER Provider Chart</Box>
        <Box {...boxInputStyles} mb={0}>
          <Controller
            render={({ field: { value, onChange, ...rest } }) => (
              <TreatingProviderInput
                {...rest}
                value={value}
                onChange={onChange}
                encounterId={encounterId}
                encounterUserType="Provider"
                userType="Provider"
                onCloseDetailsModal={onClose}
                disabled={isReadOnly}
              />
            )}
            name="provider"
            control={control}
          />
        </Box>

        <Box {...boxInputStyles} mb={0}>
          <Controller
            render={({ field: { value, onChange, ...rest } }) => (
              <SupervisorInput
                {...rest}
                value={value}
                onChange={onChange}
                encounterId={encounterId}
                encounterUserType="Supervisor"
                userType="SupervisingProvider"
                onCloseDetailsModal={onClose}
                disabled={isReadOnly}
              />
            )}
            name="supervisor"
            control={control}
          />
        </Box>
        <Box {...boxInputStyles}>
          <Controller
            render={({ field: { value, onChange, ...rest } }) => (
              <ScribeInput
                {...rest}
                value={value}
                onChange={onChange}
                encounterId={encounterId}
                encounterUserType="Scribe"
                userType="Scribe"
                onCloseDetailsModal={onClose}
                disabled={isReadOnly}
              />
            )}
            name="scribe"
            control={control}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box>Observation Nurse Chart</Box>
      <Box {...boxInputStyles} mb={0}>
        <Controller
          render={({ field: { value, onChange, ...rest } }) => (
            <NurseInput
              {...rest}
              value={value}
              onChange={onChange}
              encounterId={encounterId}
              encounterUserType="ObservationNurse"
              userType="Nurse"
              onCloseDetailsModal={onClose}
              disabled={isReadOnly}
            />
          )}
          name="observationNurse"
          control={control}
        />
      </Box>
      <Box {...boxInputStyles}>
        <Controller
          render={({ field: { value, onChange, ...rest } }) => (
            <ClinicalSupportInput
              {...rest}
              value={value}
              onChange={onChange}
              encounterId={encounterId}
              encounterUserType="ObservationClinicalSupport"
              userType="ClinicalSupport"
              onCloseDetailsModal={onClose}
              disabled={isReadOnly}
            />
          )}
          name="observationClinicalSupport"
          control={control}
        />
      </Box>

      <Box>Observation Provider Chart</Box>
      <Box {...boxInputStyles} mb={0}>
        <Controller
          render={({ field: { value, onChange, ...rest } }) => (
            <TreatingProviderInput
              {...rest}
              value={value}
              onChange={onChange}
              encounterId={encounterId}
              encounterUserType="ObservationProvider"
              userType="Provider"
              onCloseDetailsModal={onClose}
              disabled={isReadOnly}
            />
          )}
          name="observationProvider"
          control={control}
        />
      </Box>

      <Box {...boxInputStyles} mb={0}>
        <Controller
          render={({ field: { value, onChange, ...rest } }) => (
            <SupervisorInput
              {...rest}
              value={value}
              onChange={onChange}
              encounterId={encounterId}
              encounterUserType="ObservationSupervisor"
              userType="SupervisingProvider"
              onCloseDetailsModal={onClose}
              disabled={isReadOnly}
            />
          )}
          name="observationSupervisor"
          control={control}
        />
      </Box>
      <Box {...boxInputStyles}>
        <Controller
          render={({ field: { value, onChange, ...rest } }) => (
            <ScribeInput
              {...rest}
              value={value}
              onChange={onChange}
              encounterId={encounterId}
              encounterUserType="ObservationScribe"
              userType="Scribe"
              onCloseDetailsModal={onClose}
              disabled={isReadOnly}
            />
          )}
          name="observationScribe"
          control={control}
        />
      </Box>
    </Box>
  );
}

function PatientDetailsModalContent(props: PatientDetailsModalContentProps) {
  const { onClose, encounterId } = props;
  const { data, isLoading, error } = usePatientDetails(encounterId);
  const toast = useToast();
  const formContext = useForm<PatientDetailsForm>();
  const { reset, control, register, getValues } = formContext;

  const { scope } = usePermissions();
  const isReadOnly = !scope("inprocess").isEditable;

  const {
    mutateAsync: UpdateChiefComplaint,
    isLoading: isUpdateChiefComplaintLoading,
  } = usePatchEncounterField({
    encounterId,
    reloadInfo: true,
    reloadSections: ["inProcess"],
  });

  const handleSaveAndClose = async () => {
    try {
      await UpdateChiefComplaint([
        { path: "chiefComplaint", value: getValues("chiefComplaint") },
      ]);
      onClose();
    } catch (err) {
      toast({
        status: "error",
        description:
          extractApiErrorMessage(err) || genericErrors.fieldSavingError,
      });
    }
  };

  React.useEffect(() => {
    if (data) {
      const {
        roomBed,
        nurse,
        provider,
        supervisor,
        chiefComplaint,
        chargeNurse,
        admittingProvider,
        observationProvider,
        huc,
        scribe,
        clinicalSupport,
        observationNurse,
        observationSupervisor,
        observationScribe,
        observationClinicalSupport,
      } = data.data;

      const prevChiefComplaint = getValues("chiefComplaint");
      reset({
        roomBed,
        nurse,
        provider,
        supervisor,
        chiefComplaint: Boolean(prevChiefComplaint)
          ? prevChiefComplaint
          : chiefComplaint,
        chargeNurse,
        admittingProvider,
        observationProvider,
        huc,
        scribe,
        clinicalSupport,
        observationNurse,
        observationSupervisor,
        observationScribe,
        observationClinicalSupport,
      });
    }
  }, [reset, data, getValues]);

  React.useEffect(() => {
    if (error) {
      toast({ description: extractApiErrorMessage(error) });
    }
  }, [error, toast]);

  if (isLoading) {
    return <Loading w="100%" />;
  }

  return data ? (
    <FormProvider {...formContext}>
      <ModalHeader
        display="flex"
        justifyContent="space-between"
        fontSize="1rem"
        padding="20px 16px 30px"
      >
        <Box flex={1} display="flex" justifyContent="flex-start">
          <Button
            variant="label"
            onClick={onClose}
            color="blue"
            isLoading={isUpdateChiefComplaintLoading}
            isDisabled={isUpdateChiefComplaintLoading}
          >
            Close
          </Button>
        </Box>
        <Heading marginLeft="5%" fontWeight="600" fontSize="1.0625rem">
          Patient Details
        </Heading>
        <Box flex={1} display="flex" justifyContent="flex-end">
          <Button
            variant="label"
            onClick={handleSaveAndClose}
            color="blue"
            isLoading={isUpdateChiefComplaintLoading}
            isDisabled={isUpdateChiefComplaintLoading}
          >
            Save
          </Button>
        </Box>
      </ModalHeader>
      <ModalBody padding="0 15px 15px">
        <VStack spacing="14px" marginBottom="35px">
          <EncounterAvatar encounterId={encounterId} w="100px" h="100px" />
          <Box fontSize="1.625rem" fontWeight="500">
            {data.data.fullName}
          </Box>
        </VStack>

        <ChiefComplaintInput encounterId={encounterId} register={register} />

        <Box {...boxInputStyles}>
          <Controller
            render={({ field: { value, ...rest } }) => (
              <RoomInput
                value={value}
                encounterId={encounterId}
                disabled={isReadOnly}
                {...rest}
              />
            )}
            name="roomBed"
            control={control}
          />
        </Box>

        <Box {...boxInputStyles}>
          <Controller
            render={({ field: { value, ...rest } }) => (
              <HUCInput
                value={value}
                encounterId={encounterId}
                encounterUserType="HUC"
                userType="HUC"
                onCloseDetailsModal={onClose}
                disabled={isReadOnly}
                {...rest}
              />
            )}
            name="huc"
            control={control}
          />
        </Box>

        <Flex mb={4} justifyContent="center">
          <Tabs variant="solid-rounded" w="100%">
            <TabList>
              <Tab w="100%">ER Charts</Tab>
              <Tab w="100%">Observation Charts</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <ProvidersForm
                  type="ER"
                  encounterId={encounterId}
                  onClose={onClose}
                />
              </TabPanel>
              <TabPanel>
                <ProvidersForm
                  type="Observation"
                  encounterId={encounterId}
                  onClose={onClose}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Flex>
      </ModalBody>
    </FormProvider>
  ) : null;
}

function PatientDetailsModal(props: PatientDetailsModalProps) {
  const { isOpen, onClose, encounterId } = props;

  return (
    <Modal
      {...props}
      motionPreset="slideInRight"
      blockScrollOnMount={false}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent
        bg="gray.200"
        maxW="unset"
        width="540px"
        containerProps={{ justifyContent: "flex-end", alignItems: "center" }}
        height="98%"
        margin="0"
        overflowY="auto"
        overflowX="hidden"
        mr="10px"
      >
        {isOpen && (
          <PatientDetailsModalContent
            onClose={onClose}
            encounterId={encounterId}
          />
        )}
      </ModalContent>
    </Modal>
  );
}

export { PatientDetailsModal };
export type { PatientDetailsForm, PatientDetailsModalProps };
